import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService, ConfigurationService, Logger, UUID} from '@nh/angular-tses';
import {environment} from 'environments/environment';
import {BehaviorSubject, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DepartureIdLookupEntry} from '../../departures/departure/departure-id-lookup.entry';
import {DepartureIdLookupService} from './departure-id-lookup.service';

@Injectable({
  providedIn: 'root'
})
export class RevisionService extends BaseService {
  private static readonly REVISION_ENDPOINT: string = `${environment.tsesConfig.apiHost}/revision`;

  private static readonly ERROR_KEY: string = 'revision-error';

  private readonly isLatestRevisionSubject: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(
    snackbar: MatSnackBar,
    configurationService: ConfigurationService,
    private _http: HttpClient,
    private _departureIdLookupService: DepartureIdLookupService
  ) {
    super(snackbar, configurationService);
  }

  public updateLatestRevisionSubject(departureId: number, revisionNumber: number) {
    this._departureIdLookupService.getLatestRevision(departureId).then(latest => {
      this.isLatestRevisionSubject.next(latest === revisionNumber);
    });
  }

  public onLatestRevisionChange() {
    return this.isLatestRevisionSubject.asObservable();
  }

  /**
   * Creates a revision from the given departure, with a comment to attach to the diary event. The returned
   * id is that of the newly created revision.
   *
   * @param departureFragmentId {UUID | string} The departure to create a revision from.
   * @param comment             {string}        The comment to be displayed in the diary.
   * @param errorMessage        {string}        An optional error message.
   * @returns                   {Promise<UUID>} The departure fragment id of the newly created revision.
   */
  public createRevision(
    departureFragmentId: UUID | string,
    comment: string,
    errorMessage: string = 'Unable to create revision. Please try again.'
  ): Promise<UUID> {
    if (departureFragmentId == null) {
      Logger.error(RevisionService.ERROR_KEY, 'Attempted to create a revision with a null departure fragment id');
      return Promise.reject('departureFragmentId was null.');
    }

    const stringDepartureFragmentId: string =
      departureFragmentId instanceof UUID ? departureFragmentId.value : departureFragmentId;

    const params: {[param: string]: string} = {
      departureFragmentId: stringDepartureFragmentId,
      revisionComment: comment
    };

    return this._http
      .post<string>(RevisionService.REVISION_ENDPOINT, null, {params})
      .pipe(
        map((revisionStringId: string) => {
          const revisionId: UUID = UUID.orThrow(revisionStringId);
          return revisionId;
        }),
        catchError((err: HttpErrorResponse) => {
          this._handleError(err, errorMessage, RevisionService.ERROR_KEY);
          return throwError(err);
        })
      )
      .toPromise();
  }

  /**
   * Returns true if the given departure fragment id is that of the departures latest revision
   *
   * @param departureFragmentId {UUID}             The departure fragment ID.
   * @returns                   {Promise<boolean>} Promise resolving to true if the departure is the latest revision.
   */
  public isLatestRevision(departureFragmentId: UUID | string): Promise<boolean> {
    const uuidDepartureFragmentId: UUID =
      departureFragmentId instanceof UUID ? departureFragmentId : UUID.orNull(departureFragmentId);

    if (!uuidDepartureFragmentId) {
      Logger.error(RevisionService.ERROR_KEY, 'departureFragmentId could not be converted to a valid UUID');
      return Promise.reject('departureFragmentId was not a valid UUID');
    }

    return this._departureIdLookupService
      .getDepartureEntry(uuidDepartureFragmentId)
      .then((lookupEntry: DepartureIdLookupEntry) => {
        return this._departureIdLookupService
          .getFragmentIdForLatestRevision(lookupEntry.departureId)
          .then((latestDepartureFragmentId: UUID) => {
            return latestDepartureFragmentId.equals(uuidDepartureFragmentId);
          });
      });
  }

  /**
   * Returns true if the given revision does exist on the given departure
   *
   * @param departureId {number}           The numerical departure id.
   * @param revision    {number}           The revision number to check againts.
   * @returns           {Promise<boolean>} Promise resolving to true if the revision is valid.
   */
  public isValidRevision(departureId: number, revision: number): Promise<boolean> {
    if (departureId == null) {
      return Promise.resolve(false);
    }

    if (revision == null || revision < 0) {
      return Promise.resolve(false);
    }

    return this._departureIdLookupService.getLatestRevision(departureId).then((latestRevision: number) => {
      return revision <= latestRevision;
    });
  }
}
